import * as React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { Icon } from "@iconify/react";
import { enqueueSnackbar } from "notistack";
import CustomerStatsSection from "./CustomerStatsSection";
import { useEffect, useState } from "react";
import CustomerDetailTabSection from "./CustomerDetailTabSection";
import CustomerDetailFooter from "./CustomerDetailFooter";
import CustomerDetailContentSection from "./CustomerDetailContentSection";
import CustomerOfficeIdModal from "./CustomerOfficeIdModal";
import CustomerVerificationRequestBanner from "./CustomerVerificationRequestBanner";
import CustomerReasonDialog from "./CustomerReasonDialog";
import CustomerVerificationReviewPanel from "./CustomerVerificationReviewPanel";
import { URLdecode, URLencode } from "../../../utils/url";
import { useLocation, useNavigate } from "react-router-dom";
import rejectBusinessVerification from "../../../controllers/user/rejectBusinessVerification";
import rejectInitialBusinessVerification from "../../../controllers/user/rejectInitialBusinessVerification";
import getBusinessChangeRequests from "../../../controllers/user/getBusinessChangeRequests";
import getPendingBusinessChangeRequest from "../../../controllers/user/getPendingBusinessChangeRequest";

const style = {
  position: "absolute",
  top: "50%",
  right: "0%",
  transform: "translate(0%, -50%)",
  width: window.innerWidth > 600 ? "80vw" : "100vw",
  bgcolor: "background.paper",
  boxShadow: 24,
};

export default function CustomerDetailModal({ open, setOpen, data, callback }) {
  const params = URLdecode();
  const [tab, setTab] = useState("profile");
  const [officeIdOpen, setOfficeIdOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [wallets, setWallets] = useState([]);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectBusy, setRejectBusy] = useState(false);
  /** Only a real Pending request — drives the amber banner. */
  const [pendingChangeRequest, setPendingChangeRequest] = useState(null);
  /** Request currently open in the review panel (pending or from History). */
  const [reviewChangeRequest, setReviewChangeRequest] = useState(null);
  const [changeRequestLoading, setChangeRequestLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const customerWanted = data?._id === params?.order || open;
  const customerVisible = customerWanted && !rejectOpen;
  /**
   * First-time: asked for verification, not verified yet, and nothing on the
   * business-change-requests list — use activate/rejectBusinessVerification.
   */
  const isInitialVerificationRequest = Boolean(
    data?.detail?.requestedVerification &&
      !data?.detail?.isVerified &&
      !pendingChangeRequest &&
      !changeRequestLoading,
  );

  const handleClose = () => {
    setReviewOpen(false);
    setRejectOpen(false);
    setReviewChangeRequest(null);
    setOpen(false);
    if (params?.order) {
      navigate(
        `${location.pathname}?${URLencode({ ...params, order: null })}`,
        { replace: true },
      );
    }
  };

  const handleOpenOfficeId = () => {
    handleClose();
    setOfficeIdOpen(true);
  };

  /** Load Pending only — amber banner needs a real pending row. */
  const loadPendingChangeRequest = React.useCallback(async (accountId) => {
    if (!accountId) {
      setPendingChangeRequest(null);
      return null;
    }
    setChangeRequestLoading(true);

    let res = await getBusinessChangeRequests(accountId, { status: "Pending" });
    if (!res?.return || !(res.data || []).length) {
      res = await getBusinessChangeRequests(accountId);
    }

    setChangeRequestLoading(false);

    if (!res?.return) {
      setPendingChangeRequest(null);
      return null;
    }

    const pending = getPendingBusinessChangeRequest(res.data || []);
    setPendingChangeRequest(pending);
    return pending;
  }, []);

  useEffect(() => {
    if (!open) {
      setReviewOpen(false);
      setRejectOpen(false);
      setReviewChangeRequest(null);
    }
  }, [open]);

  useEffect(() => {
    setReviewOpen(false);
    setRejectOpen(false);
    setPendingChangeRequest(null);
    setReviewChangeRequest(null);
  }, [data?._id]);

  useEffect(() => {
    if (!customerWanted || !data?._id) {
      setPendingChangeRequest(null);
      return;
    }
    loadPendingChangeRequest(data._id);
  }, [customerWanted, data?._id, loadPendingChangeRequest]);

  const openReview = async (request) => {
    if (request) {
      setReviewChangeRequest(request);
      setReviewOpen(true);
      return;
    }
    let pending = pendingChangeRequest;
    if (!pending && data?._id) {
      pending = await loadPendingChangeRequest(data._id);
    }
    if (pending) {
      setReviewChangeRequest(pending);
      setReviewOpen(true);
      return;
    }
    // First-time verification — no business-change request row
    if (
      data?.detail?.requestedVerification &&
      !data?.detail?.isVerified
    ) {
      setReviewChangeRequest(null);
      setReviewOpen(true);
      return;
    }
    enqueueSnackbar("No pending verification request to review.", {
      variant: "warning",
    });
  };

  const confirmReject = async (reason) => {
    const trimmed = String(reason || "").trim();
    if (!trimmed) {
      enqueueSnackbar("Add a short reason so the agent knows what to fix.", {
        variant: "warning",
      });
      return;
    }
    if (!data?._id) return;

    setRejectBusy(true);
    const request = reviewChangeRequest || pendingChangeRequest;
    const requestId = request?._id || request?.id;

    let res;
    if (requestId) {
      res = await rejectBusinessVerification(data._id, requestId, trimmed);
    } else if (
      data?.detail?.requestedVerification &&
      !data?.detail?.isVerified
    ) {
      // First-time — old activate/reject route
      res = await rejectInitialBusinessVerification(data._id, trimmed);
    } else {
      setRejectBusy(false);
      enqueueSnackbar(
        "No pending verification request found for this account.",
        { variant: "error" },
      );
      return;
    }

    setRejectBusy(false);
    if (!res?.return) {
      enqueueSnackbar(res?.msg || "Couldn't reject this request.", {
        variant: "error",
      });
      return;
    }
    enqueueSnackbar("Verification request rejected.", { variant: "success" });
    setRejectOpen(false);
    setReviewOpen(false);
    setReviewChangeRequest(null);
    setPendingChangeRequest(null);
    loadPendingChangeRequest(data._id);
    callback?.(res.data);
  };

  return (
    <>
      <CustomerOfficeIdModal
        open={officeIdOpen}
        setOpen={setOfficeIdOpen}
        data={data}
      />
      <CustomerReasonDialog
        open={rejectOpen}
        title="Reject verification request"
        description={
          data?.detail?.isVerified
            ? "Their current verified details stay as they are. They'll see your reason and can fix the change, then request verification again."
            : "The agency stays unverified. They'll see your reason and can fix it, then request verification again."
        }
        placeholder="e.g. CAC certificate is cut off — upload a clearer full-page scan."
        confirmLabel="Reject request"
        cancelLabel="Keep pending"
        loading={rejectBusy}
        onClose={() => {
          if (!rejectBusy) setRejectOpen(false);
        }}
        onConfirm={confirmReject}
      />
      <Modal
        open={customerVisible}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <div className="flex flex-col font-main h-screen max-w-[100vw]">
            <div className="w-full max-w-[100vw] bg-primary1 p-4 text-white font-semibold flex justify-between">
              <span className="flex min-w-0 items-center gap-2">
                {reviewOpen ? (
                  <button
                    type="button"
                    onClick={() => {
                      setReviewOpen(false);
                      setReviewChangeRequest(null);
                    }}
                    className="inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-1 text-sm font-semibold text-white/90 hover:bg-white/10"
                    aria-label="Back to customer"
                  >
                    <Icon icon="mdi:arrow-left" width={20} height={20} />
                    Back
                  </button>
                ) : null}
                <span className="truncate">
                  {reviewOpen
                    ? `Review · ${data?.detail?.agencyName || "Business"}`
                    : data?.detail?.agencyName}
                </span>
              </span>
              <button type="button" onClick={() => handleClose()}>
                <Icon
                  icon="material-symbols:close"
                  className={`text-secondary text-3xl`}
                />
              </button>
            </div>

            {reviewOpen ? (
              <CustomerVerificationReviewPanel
                data={data}
                changeRequest={reviewChangeRequest}
                changeRequestLoading={changeRequestLoading}
                initialVerification={
                  isInitialVerificationRequest && !reviewChangeRequest
                }
                onReject={() => setRejectOpen(true)}
                onApproved={(account) => {
                  setReviewOpen(false);
                  setReviewChangeRequest(null);
                  loadPendingChangeRequest(data?._id);
                  callback?.(account);
                }}
              />
            ) : (
              <div className="flex flex-1 flex-col overflow-y-scroll scroll-hide relative">
                <CustomerVerificationRequestBanner
                  changeRequest={pendingChangeRequest}
                  initialVerification={isInitialVerificationRequest}
                  onReview={() =>
                    openReview(pendingChangeRequest || null)
                  }
                />
                <CustomerStatsSection
                  data={data}
                  open={open}
                  wallets={wallets}
                  setWallets={setWallets}
                />
                <CustomerDetailTabSection
                  selected={tab}
                  onTabSelect={(tb) => setTab(tb)}
                />
                <CustomerDetailContentSection
                  tab={tab}
                  data={data}
                  callback={callback}
                  onOpenChangeRequest={(request) => openReview(request)}
                />
                <div className="flex-1"></div>
                <CustomerDetailFooter
                  data={data}
                  callback={callback}
                  wallets={wallets}
                  onOpenOfficeId={handleOpenOfficeId}
                  onOpenReview={
                    pendingChangeRequest || isInitialVerificationRequest
                      ? () => openReview(pendingChangeRequest || null)
                      : data?.detail?.isVerified
                        ? () => {
                            setReviewChangeRequest(null);
                            setReviewOpen(true);
                          }
                        : undefined
                  }
                />
              </div>
            )}
          </div>
        </Box>
      </Modal>
    </>
  );
}
